import { useEffect, useRef } from 'react';
import type Lenis from 'lenis';

let lenisInstance: Lenis | null = null;
let modulesRequest: Promise<[
  typeof import('lenis'), typeof import('gsap'), typeof import('gsap/ScrollTrigger'),
]> | undefined;

export function useLenis() {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const isTouchDevice =
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches;

    if (isTouchDevice) {
      lenisInstance = null;
      lenisRef.current = null;
      return;
    }

    let cancelled = false;
    let destroy: (() => void) | undefined;
    let initialize: (() => void) | undefined;
    // A menu/modal can acquire the shared body lock while these chunks load.
    // Wait to create Lenis rather than starting an instance the lock never stopped.
    const lockObserver = new MutationObserver(() => initialize?.());
    lockObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });

    const request = modulesRequest ??= Promise.all([
      import('lenis'),
      import('gsap'),
      import('gsap/ScrollTrigger'),
    ]);
    void request.then(([{ default: LenisConstructor }, { gsap }, { ScrollTrigger }]) => {
      initialize = () => {
        if (cancelled || destroy || document.body.style.overflow === 'hidden') return;
        try {
          gsap.registerPlugin(ScrollTrigger);
          const lenis = new LenisConstructor({
            duration: 0.5,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 2,
          });
          lenisInstance = lenis;
          lenisRef.current = lenis;
          const updateScrollTrigger = () => ScrollTrigger.update();
          const tickLenis = (time: number) => lenis.raf(time * 1000);
          destroy = () => {
            lenis.off('scroll', updateScrollTrigger);
            gsap.ticker.remove(tickLenis);
            lenis.destroy();
            if (lenisInstance === lenis) lenisInstance = null;
            if (lenisRef.current === lenis) lenisRef.current = null;
          };
          lenis.on('scroll', updateScrollTrigger);
          gsap.ticker.add(tickLenis);
          gsap.ticker.lagSmoothing(0);
          lockObserver.disconnect();
        } catch {
          initialize = undefined;
          lockObserver.disconnect();
          destroy?.();
        }
      };
      initialize();
    }).catch(() => {
      if (modulesRequest === request) modulesRequest = undefined;
      // Failed optional chunks leave native scrolling available.
      initialize = undefined;
      lockObserver.disconnect();
      destroy?.();
    });

    return () => {
      cancelled = true;
      lockObserver.disconnect();
      destroy?.();
    };
  }, []);

  return lenisRef;
}

export function getLenis() {
  return lenisInstance;
}
